import Bugsnag, { type Event as BugsnagEvent } from '@bugsnag/js'
import { isAxiosError } from 'axios'
import { startOfMonth, endOfMonth } from 'date-fns'

import { ensureError } from 'utils/ensureError'

import type { ExternalReviewSettings } from './ExternalReviewSettings'
import type { UpcomingPosts } from './UpcomingPosts'
import {
  ExternalReviewApiError,
  ExternalReviewGeneratedClient,
} from './generated-client'
import type { paths } from './generated/sked-api-openapi-types'

function getGeneratedClient() {
  return new ExternalReviewGeneratedClient<paths>()
}

/**
 * Report an error to Bugsnag only when it is a server-side (5xx) failure.
 * Client errors (4xx) are expected conditions (bad review key, expired link,
 * validation failures) and should not create noise in Bugsnag.
 */
function notifyIfServerError(
  error: unknown,
  onEvent?: (event: BugsnagEvent) => void
): void {
  const status = getErrorStatus(error)
  if (status !== undefined && status >= 400 && status < 500) {
    return
  }
  const errorObj = ensureError(error)
  if (onEvent) {
    Bugsnag.notify(errorObj, onEvent)
  } else {
    Bugsnag.notify(errorObj)
  }
}

/**
 * Returns true when the API responds with 422 "Status key should differ from
 * the current one" — i.e. the post already has the requested status.
 * This is a no-op condition, not an application error.
 *
 * NOTE: The check relies on the exact error message string returned by the
 * External Review API (`{"error":true,"message":"Status key should differ
 * from the current one"}`). If the API changes this message this guard will
 * stop matching and the 422 will be treated as a real error again, which is
 * the safe fallback behaviour.
 *
 * `ExternalReviewApiError.body` is always a `string` — it is populated by
 * `stringifyOpenApiError` in generated-client.ts which always returns a
 * string, so the `instanceof` check already guarantees the type.
 */
function isStatusUnchangedError(error: unknown): boolean {
  return (
    error instanceof ExternalReviewApiError &&
    error.status === 422 &&
    error.body.includes('Status key should differ from the current one')
  )
}

function getErrorStatus(error: unknown): number | undefined {
  if (isAxiosError(error)) {
    return error.response?.status
  }

  if (
    typeof error === 'object' &&
    error !== null &&
    'response' in error &&
    typeof error.response === 'object' &&
    error.response !== null &&
    'status' in error.response &&
    typeof error.response.status === 'number'
  ) {
    return error.response.status
  }

  return undefined
}

const fetchPosts = async (reviewKey: string) => {
  try {
    const generatedClient = getGeneratedClient()
    const data = await generatedClient.unwrap(
      await generatedClient.client.GET(
        '/v2/collaboration/external-review/{reviewKey}/posts',
        {
          params: { path: { reviewKey } },
        }
      )
    )

    return data as UpcomingPosts[]
  } catch (error) {
    notifyIfServerError(error)
    throw error
  }
}

const fetchPostPreview = async (reviewKey: string, postId: string) => {
  try {
    const generatedClient = getGeneratedClient()
    const data = await generatedClient.unwrap(
      await generatedClient.client.GET(
        '/v2/collaboration/external-review/{reviewKey}/posts/{postId}/preview',
        {
          params: { path: { reviewKey, postId } },
        }
      )
    )

    return data
  } catch (error) {
    notifyIfServerError(error)
    throw error
  }
}

const fetchPostsPreview = async (reviewKey: string) => {
  try {
    const generatedClient = getGeneratedClient()
    const data = await generatedClient.unwrap(
      await generatedClient.client.GET(
        '/v2/collaboration/external-review/{reviewKey}/posts/preview',
        {
          params: { path: { reviewKey } },
        }
      )
    )

    return data as unknown[]
  } catch (error) {
    notifyIfServerError(error)
    throw error
  }
}

const fetchPostSkedPreview = async (reviewKey: string, postId: string) => {
  try {
    const generatedClient = getGeneratedClient()
    const data = await generatedClient.unwrap(
      await generatedClient.client.GET(
        '/v2/collaboration/external-review/{reviewKey}/posts/{postId}/preview',
        {
          params: { path: { reviewKey, postId } },
        }
      )
    )

    return data
  } catch (error) {
    notifyIfServerError(error)
    throw error
  }
}

const fetchSetting = async (
  reviewKey: string
): Promise<ExternalReviewSettings> => {
  try {
    const generatedClient = getGeneratedClient()
    const data = await generatedClient.unwrap(
      await generatedClient.client.GET(
        '/v2/collaboration/external-review/{reviewKey}/settings',
        {
          params: { path: { reviewKey } },
        }
      )
    )
    return data as ExternalReviewSettings
  } catch (error) {
    notifyIfServerError(error, function (event) {
      event.addMetadata('url', {
        url: `/v2/collaboration/external-review/${reviewKey}/settings`,
        payload: {
          reviewKey,
        },
      })
    })
    throw ensureError(error)
  }
}

const postAction = async (
  reviewKey: string,
  postId: string,
  statusKey: string
) => {
  try {
    const generatedClient = getGeneratedClient()
    const data = await generatedClient.unwrap(
      await generatedClient.client.PUT(
        '/v2/collaboration/external-review/{reviewKey}/{postId}/status/{statusKey}',
        {
          params: { path: { reviewKey, postId, statusKey } },
        }
      )
    )

    return data
  } catch (error) {
    // When the post already has this status the API returns 422 with
    // "Status key should differ from the current one". Treat this as a
    // no-op — log a warning and return gracefully so the UI is not disrupted.
    if (isStatusUnchangedError(error)) {
      console.warn(
        `[external-review] postAction: status update skipped — post ${postId} already has status key "${statusKey}"`
      )
      return null
    }
    notifyIfServerError(error, function (event) {
      event.addMetadata('url', {
        url: `/v2/collaboration/external-review/${reviewKey}/${postId}/status/${statusKey}`,
        payload: {
          post_status_key: statusKey,
        },
      })
    })
    throw ensureError(error)
  }
}

const postComment = async (
  reviewKey: string,
  postId: string,
  comment: string,
  commentId: string
) => {
  try {
    const generatedClient = getGeneratedClient()
    const data = await generatedClient.unwrap(
      await generatedClient.client.PATCH(
        '/v2/collaboration/external-review/{reviewKey}/{postId}/comments/{commentId}',
        {
          params: { path: { reviewKey, postId, commentId } },
          body: { text: comment },
        }
      )
    )

    return data
  } catch (error) {
    notifyIfServerError(error, function (event) {
      event.addMetadata('url', {
        url: `/v2/collaboration/external-review/${reviewKey}/${postId}/comments/${commentId}`,
        payload: {
          text: comment,
        },
      })
    })
    throw ensureError(error)
  }
}

const fetchComment = async (reviewKey: string, postId: string) => {
  try {
    const generatedClient = getGeneratedClient()
    const data = await generatedClient.unwrap(
      await generatedClient.client.GET(
        '/v2/collaboration/external-review/{reviewKey}/{postId}/comments',
        {
          params: { path: { reviewKey, postId } },
        }
      )
    )

    return data
  } catch (error) {
    notifyIfServerError(error)
    throw error
  }
}

// for ig only, desktop only
const fetchGridPreview = async (reviewKey: string, postId: string) => {
  try {
    const generatedClient = getGeneratedClient()
    const data = await generatedClient.unwrap(
      await generatedClient.client.GET(
        '/v2/collaboration/external-review/{reviewKey}/posts/{postId}/grid-preview',
        {
          params: { path: { reviewKey, postId } },
        }
      )
    )

    return data
  } catch (error) {
    notifyIfServerError(error)
    throw error
  }
}

// for mobile grid preview
const fetchGridPreviewByAccount = async (
  reviewKey: string,
  accountId: string
) => {
  try {
    const generatedClient = getGeneratedClient()
    const data = await generatedClient.unwrap(
      await generatedClient.client.GET(
        '/v2/collaboration/external-review/{reviewKey}/accounts/{accountId}/grid-preview',
        {
          params: { path: { reviewKey, accountId } },
        }
      )
    )

    return data
  } catch (error) {
    notifyIfServerError(error)
    throw error
  }
}

// desktop only
const fetchCalendar = async (reviewKey: string, date: Date) => {
  try {
    const generatedClient = getGeneratedClient()
    const data = await generatedClient.unwrap(
      await generatedClient.client.GET(
        '/v2/collaboration/external-review/{reviewKey}/posts',
        {
          params: {
            path: { reviewKey },
            query: {
              excludeDrafts: true,
              dateFrom: startOfMonth(date).toISOString(),
              dateTo: endOfMonth(date).toISOString(),
            },
          },
        }
      )
    )

    return data
  } catch (error) {
    notifyIfServerError(error)
    throw error
  }
}

export {
  fetchSetting,
  fetchPosts,
  fetchPostPreview,
  fetchPostsPreview,
  fetchGridPreview,
  fetchGridPreviewByAccount,
  fetchPostSkedPreview,
  postAction,
  postComment,
  fetchComment,
  fetchCalendar,
}
